import React, {useState} from 'react';
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Alert,
    Paper,
    Avatar,
    InputAdornment,
    IconButton
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {Email, LockOutlined, Visibility, VisibilityOff, AdminPanelSettings} from '@mui/icons-material';
import {adminLogin} from '../../services/api';
import logo from '../../store_logo/zebra.jpg';

function AdminLogin() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            console.log('Attempting login with:', {email, password: '***'});
            const response = await adminLogin({email, password});
            console.log('Login response received:', response);

            if (!response.token) {
                console.error('No token in response:', response);
                setError('No token received from server');
                setLoading(false);
                return;
            }

            console.log('Token received:', response.token);
            localStorage.setItem('admin_token', response.token);

            const savedToken = localStorage.getItem('admin_token');
            console.log('Verified saved token:', savedToken);

            await new Promise(resolve => setTimeout(resolve, 100));

            // Only navigate on successful login
            navigate('/admin', {replace: true});
        } catch (error) {
            console.error('Login failed:', error);
            console.error('Error response:', error.response);
            console.error('Error response data:', error.response?.data);

            let errorMessage = 'Login failed. Please try again.';

            if (error.response?.data) {
                errorMessage = error.response.data.message || error.response.data.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Unable to connect to server. Please check your internet connection.';
            } else if (error.message) {
                errorMessage = error.message;
            }

            console.log('Setting error message:', errorMessage);
            setError(errorMessage);
            setLoading(false);
        }
    };

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        if (error) setError('');
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        if (error) setError('');
    };

    const handleTogglePassword = () => {
        setShowPassword(!showPassword);
    };

    return (
        <Container maxWidth="sm">
            <Box
                sx={{
                    mt: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 3
                }}
            >
                <Paper
                    elevation={6}
                    sx={{
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        borderRadius: 2,
                        width: '100%',
                        background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)'
                    }}
                >
                    <Avatar
                        sx={{
                            width: 80,
                            height: 80,
                            mb: 2,
                            bgcolor: 'primary.main',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                    >
                        <AdminPanelSettings sx={{fontSize: 50}}/>
                    </Avatar>

                    <Typography component="h1" variant="h4" fontWeight="bold" sx={{mb: 1}}>
                        Admin Portal
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{mb: 3}}>
                        Sign in to manage Zebra Store
                    </Typography>

                    {error && (
                        <Alert
                            severity="error"
                            onClose={() => setError('')}
                            sx={{
                                mb: 2,
                                width: '100%',
                                animation: 'shake 0.5s'
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{width: '100%'}}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            value={email}
                            onChange={handleEmailChange}
                            error={!!error}
                            autoFocus
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email color={error ? "error" : "primary"}/>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{mb: 2}}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            value={password}
                            onChange={handlePasswordChange}
                            error={!!error}
                            autoComplete="current-password"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlined color={error ? "error" : "primary"}/>
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleTogglePassword} edge="end">
                                            {showPassword ? <VisibilityOff/> : <Visibility/>}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{mb: 3}}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            sx={{
                                mt: 2,
                                mb: 2,
                                py: 1.5,
                                borderRadius: 1.5,
                                fontWeight: 'bold',
                                fontSize: '1rem',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
                                }
                            }}
                        >
                            {loading ? 'Signing in...' : 'Sign In'}
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
}

export default AdminLogin;
